import { useQueryClient } from "@tanstack/react-query"
import axios from "axios"

export const isQuestionnaireNotFound = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  "response" in error &&
  (error.response as { status?: number } | undefined)?.status === 404

export const retryQuestionnaire = (
  failureCount: number,
  error: Error,
  fallback: boolean | number | ((count: number, error: Error) => boolean) = 3,
): boolean => {
  if (isQuestionnaireNotFound(error) || axios.isCancel(error)) return false
  if (typeof fallback === "function") return fallback(failureCount, error)
  if (typeof fallback === "boolean") return fallback
  return failureCount < fallback
}

type QuestionnaireRetry = Parameters<typeof retryQuestionnaire>[2]

/**
 * The `retry` option for a questionnaire query: a missing questionnaire and a cancelled request
 * are terminal, and every other failure follows the caller's `retry`, then the QueryClient's
 * default, then 3 (the library's own). Overriding the provider's choice would make a client
 * configured with `retry: false` retry a server error three times.
 */
export const useQuestionnaireRetry = (configured?: QuestionnaireRetry) => {
  const queryClient = useQueryClient()
  const fallback =
    configured ?? queryClient.getDefaultOptions().queries?.retry ?? 3
  return (failureCount: number, error: Error): boolean =>
    retryQuestionnaire(failureCount, error, fallback)
}
